import { ExpiredAccessTokenHandler } from 'diam-connector/handlers/expired-access-token-handler';
import { TokenConnector } from 'diam-connector/connectors/token-connector';
import { Token } from 'diam-connector/models/token';
import { StatusCodeError } from 'diam-connector/errors/statuscode-error';
import * as config from 'config';
import { emitSessionUpdated } from 'utils/sessionEvents';
import type { Session } from 'models/auth';
import { JsonApiErrorDto } from 'models/jsonApiError';

/**
 * Handler to refresh expired access tokens using the refresh token.
 *
 */
export class RefreshExpiredAccessTokenHandler implements ExpiredAccessTokenHandler {
  private static readonly inFlightByRefreshToken = new Map<
    string,
    Promise<Token>
  >();

  constructor(
    endpoint: string,
    private readonly connector = new TokenConnector({ endpoint })
  ) {}

  public async handle(token: Token): Promise<Token> {
    const { refreshToken } = token;
    if (!refreshToken) {
      throw new Error('Refresh token not found');
    }

    const existing =
      RefreshExpiredAccessTokenHandler.inFlightByRefreshToken.get(refreshToken);
    if (existing) {
      return existing;
    }

    // diam-connector's Token.clientId can be optional; fall back to app client id.
    const clientId = token.clientId ?? config.clientId;

    const refreshPromise = this.refreshExpiredToken(refreshToken, clientId);

    RefreshExpiredAccessTokenHandler.inFlightByRefreshToken.set(
      refreshToken,
      refreshPromise
    );

    return refreshPromise;
  }

  public shouldHandle(token: Token, error: StatusCodeError): boolean {
    // Need a refresh token.
    // If there is no token or refresh token, we cannot refresh the access token, so we should not handle.
    if (!token?.refreshToken) {
      return false;
    }

    // The error_code should be 'invalid.token'.
    return error.code === 'invalid.token';
  }

  private async refreshExpiredToken(
    refreshToken: string,
    clientId: string
  ): Promise<Token> {
    try {
      const refreshedToken = await this.connector.loginWithRefreshToken(
        refreshToken,
        clientId
      );

      const session: Session = {
        accessToken: refreshedToken.accessToken,
        refreshToken: refreshedToken.refreshToken ?? refreshToken,
        userId: refreshedToken.userId
      };

      // Emit session updated event so store and localStorage stay in sync.
      // This is needed to ensure future API calls use the new token.
      emitSessionUpdated(session);

      return refreshedToken;
    } catch {
      // Re-throw as invalid.token error to trigger appropriate handling (clearing storage, redirecting to login, etc).
      throw new JsonApiErrorDto('Refresh token not found', {
        code: 'invalid.token',
        status: 401
      });
    } finally {
      RefreshExpiredAccessTokenHandler.inFlightByRefreshToken.delete(
        refreshToken
      );
    }
  }
}
